import { func, number, object, oneOfType, string } from "prop-types";
import { useEffect, useState } from "react";

import { useHandleUploadFile } from "ExtendedFields/API/queryHooks";

import FnrUploadButton from "UI/FnrUploadButton";

import ExtendedFieldErrorMessage from "./ExtendedFieldErrorMessage";
import { Label, RequiredIcon } from "./styles";

const UploadField = ({
  field = {},
  error,
  onChange: setUploadCommand, // Aliasing this for clarity. This is being used to pass upload instructions up the tree.
  userID,
  value,
}) => {
  const { title, inputIsRequired, fieldID } = field;
  const [currentFile, setCurrentFile] = useState();
  const [hasUploaded, setHasUploaded] = useState(false);
  const [cachedRes, setCachedRes] = useState();
  const [originalValue, setOriginalValue] = useState(value);

  useEffect(() => {
    // since originalValue is updated on upload, we can set the value back to something display friendly on unmount.
    // If you are attempting to execute the value as a function but it is a string before you call it, this may be why. 🙄 Welcome to signups land; I didn't have a better idea.
    return () => setUploadCommand(originalValue);
  }, [setUploadCommand, originalValue]);

  const {
    handleFileUpload,
    isUploading,
    errorMessage: localError,
    clearUploadErrorMessage,
    progressPercentage,
  } = useHandleUploadFile({
    targetUserID: userID,
    networkFieldID: fieldID,
    file: currentFile,
  });

  const handleOnChange = (file) => {
    clearUploadErrorMessage();
    setCurrentFile(file);
  };

  // sync upload function up the tree to be uploading on submit.
  useEffect(() => {
    // Don't show validation message when not populated and not required or editing. This will instruct isValidFieldResponse to not upload the file. (Signups/SignupsPublicPage/SignupItem/utils/fieldValidationHelpers.js)
    // We skip uploading when editing if the file has not changed, because this means the file previously uploaded should continue being used.
    if (!inputIsRequired && !currentFile) {
      setUploadCommand(null);
      return;
    }
    setUploadCommand(async () => {
      if (!hasUploaded) {
        const res = await handleFileUpload();
        if (res?.success) {
          setHasUploaded(true);
          setOriginalValue(res.response.fileName);
        }
        setCachedRes(res);
        return res;
      }
      return cachedRes;
    });
  }, [
    handleFileUpload,
    setUploadCommand,
    currentFile,
    hasUploaded,
    cachedRes,
    inputIsRequired,
  ]);

  // Set hasUploaded to false if the filed changes
  useEffect(() => {
    setHasUploaded(false);
  }, [currentFile]);

  return (
    <div>
      <Label>
        {title}
        {inputIsRequired && <RequiredIcon />}
      </Label>
      <FnrUploadButton
        documentName={title}
        onChange={handleOnChange}
        progressPercentage={progressPercentage}
      />
      {Boolean(localError || (error && inputIsRequired)) && (
        <ExtendedFieldErrorMessage>
          {localError || error}
        </ExtendedFieldErrorMessage>
      )}
    </div>
  );
};

UploadField.propTypes = {
  field: object.isRequired,
  error: string,
  onChange: func.isRequired,
  userID: number,
  value: oneOfType([string, func]),
};

export default UploadField;
